import { useState, useEffect, useMemo } from 'react';
import relocationApi from '../services/relocationApi';

/**
 * "Which of these guests are not in the unit their booking names?" — for a whole list, in one call.
 * tasks/guest-relocation-v1.md §4.6 · Decisions: docs/03_Technical/GUEST_RELOCATION_DESIGN.md
 *
 * A hook rather than the same effect written twice: the arrivals view and the inbox ask the
 * identical question, and the part that is easy to get wrong is not the fetch — it is what a
 * FAILURE means. Silence would render as "nobody has been moved", which is the one wrong answer
 * this whole surface exists to prevent, so the failure is reported and each caller decides how loud
 * to be about it.
 *
 * @param {string[]} bookingIds  ids from the rows on screen; may contain nulls, they are dropped
 * @param {boolean}  enabled     false for a user who may not ask (the endpoint is core-staff only)
 * @returns {{ relocations: Object, failed: boolean, loading: boolean }}
 *          `relocations[bookingId]` is the move or null. ⚠️ A key that is ABSENT means we have not
 *          asked about it — not that the guest is in place.
 */
export default function useRelocations(bookingIds, enabled = true) {
  const [relocations, setRelocations] = useState({});
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  // The ids as a stable string. Without this the effect re-runs on every render — a list rebuilds
  // its array each time even when nothing about it changed, and the fetch would loop forever.
  const key = useMemo(
    () => [...new Set((bookingIds || []).filter(Boolean).map(String))].sort().join(','),
    [bookingIds]
  );

  useEffect(() => {
    if (!enabled || !key) { setRelocations({}); setFailed(false); return undefined; }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    relocationApi.getCurrentBatch(key.split(','))
      .then((data) => {
        if (cancelled) return;
        if (data?.success) setRelocations(data.relocations || {});
        else setFailed(true);          // a 200 without an answer is still a failure to answer
      })
      .catch((err) => {
        console.error('Error fetching relocations for list:', err);
        if (!cancelled) setFailed(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [key, enabled]);

  return { relocations, failed, loading };
}
